/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo } from 'react';
import ListSubheader from '@mui/material/ListSubheader';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import Box from '@mui/material/Box';
import { educationalProgramsGroups, icons, appRoutes } from 'router/router';
import { useHistory, useLocation } from 'react-router';
import { getUserId } from 'utils/sessionStorage';
import { useAppSelector } from 'app/hooks';
import { STRIPE_PRICE_NAME } from 'constants/common';

// Menu for Student only
export default function EducationalProgramsList() {
  let history = useHistory();
  let location = useLocation();
  const [open, setOpen] = React.useState<string | null>(null);
  let studentId = getUserId();
  const stripeSubscriptionInfo = useAppSelector((store) => store.basicInformation.stripeSubscriptionInfo);
  
  const isUpgrade = stripeSubscriptionInfo?.subcription_plan === STRIPE_PRICE_NAME.GradMapStarter;

  // Always hide "Upgrade to Silver+" from menu; upgrade is shown via overlay when isUpgrade
  const filteredEducationalProgramsGroups = useMemo(() => {
    return educationalProgramsGroups.map((route: any) => {
      if (route.isGroup && route.children) {
        const filteredChildren = route.children.filter((child: any) => child?.name !== 'Upgrade to Silver+');
        return { ...route, children: filteredChildren };
      }
      return route;
    });
  }, []);
  
  useEffect(() => {
    let found = false;
    filteredEducationalProgramsGroups.forEach((route: any) => {
      if (
        route.children.find((e: any) => location?.pathname?.includes(e?.path?.replace('/:id', '')))
      ) {
        setOpen(route.name);
        found = true;
      }
    });
    // Auto-open menu if no match found but component is mounted (user clicked Educational Programs button)
    if (!found && filteredEducationalProgramsGroups.length > 0) {
      setOpen(filteredEducationalProgramsGroups[0].name);
    }
  }, [location, filteredEducationalProgramsGroups]);

  const handleClick = (route: any) => {
    if (open === route.name) return setOpen(null);
    setOpen(route.name);
    if (route.isGroup === false) {
      history.push(route.route.path);
    }
  };

  // Ensure we have data
  if (!filteredEducationalProgramsGroups || filteredEducationalProgramsGroups.length === 0) {
    return <div>No educational programs available</div>;
  }

  return (
    <List
      sx={{
        width: '100%',
        maxWidth: 360,
        bgcolor: 'background.paper',
        height: '100%',
        overflow: 'auto',
        display: 'block',
        visibility: 'visible',
      }}
      component="nav"
      subheader={
        <ListSubheader component="div" className="text-field-light text-center">
          {/* All changes saved <SaveOutlinedIcon fontSize="small" /> */}
        </ListSubheader>
      }
    >
      {filteredEducationalProgramsGroups.map((route: any, index: any) => {
        const IconBefore = icons[route.icon] || 'Component';
        return (
          <div key={index}>
            <ListItemButton
              onClick={() => handleClick(route)}
              className={`px-1 my-1 ${open === route.name && 'bg-light fw-bold'}`}
            >
              <IconBefore fontSize="small" className="me-2 text-secondary" />
              <span className="text-field-mid">{route.name}</span>
              {route.isGroup &&
                (open === route.name ? (
                  <ExpandLess fontSize="small" className="text-field-light" />
                ) : (
                  <ExpandMore fontSize="small" className="text-field-light" />
                ))}
            </ListItemButton>
            {route.isGroup && (
              <Collapse in={open === route.name} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {(() => {
                    const isEducationalPrograms = route.name === 'Educational Programs';
                    const isVisibleChild = (child: any) => {
                      const p = child?.path?.replace(':id', '') || '';
                      // Free plan: Discover and My programs (it tracks 2 programs; the limit itself is the gate, Michelle Oct 31).
                      return p === '/summer-programs' || p === '/summer-programs/saved' || p === '/summer-programs/manager';
                    };
                    const visibleChildren = isUpgrade && isEducationalPrograms
                      ? route.children.filter((c: any) => isVisibleChild(c))
                      : route.children;
                    const lockedChildren = isUpgrade && isEducationalPrograms
                      ? route.children.filter((c: any) => !isVisibleChild(c))
                      : [];

                    const renderChildItem = (child: any, i: number, options?: { locked?: boolean }) => {
                      const childPath = child?.path.replace(':id', '');
                      let isActive = false;
                      if (childPath === '/summer-programs') {
                        isActive = location.pathname === '/summer-programs' ||
                          (location.pathname.startsWith('/summer-programs/') &&
                            !location.pathname.startsWith('/summer-programs/saved') &&
                            !location.pathname.startsWith('/summer-programs/manager') &&
                            !location.pathname.startsWith('/summer-programs/requirements'));
                      } else if (childPath === '/summer-programs/saved') {
                        isActive = location.pathname.startsWith('/summer-programs/saved');
                      } else if (childPath === '/summer-programs/manager') {
                        isActive = location.pathname.startsWith('/summer-programs/manager');
                      } else {
                        isActive = location.pathname === childPath || location.pathname.startsWith(childPath + '/');
                      }
                      const ChildIcon = child?.icon ? icons[child.icon] : null;
                      const locked = options?.locked ?? false;

                      return (
                        <ListItemButton
                          key={i}
                          sx={{
                            pl: 2,
                            pt: 1.5,
                            pb: 1.5,
                            mb: 0.5,
                            borderRadius: '8px',
                            mx: 1,
                            backgroundColor: isActive && !locked ? 'rgba(0, 123, 255, 0.1)' : 'transparent',
                            '&:hover': {
                              backgroundColor: locked ? 'transparent' : 'rgba(0, 123, 255, 0.05)',
                            },
                            ...(locked && {
                              pointerEvents: 'none',
                              opacity: 0.4,
                            }),
                          }}
                          onClick={() => !locked && history.push(child?.path.replace(':id', studentId))}
                          disabled={locked}
                        >
                          {ChildIcon && (
                            <ChildIcon
                              fontSize="small"
                              className="me-2"
                              sx={{
                                color: isActive ? '#007bff' : '#6c757d',
                              }}
                            />
                          )}
                          <span
                            className={isActive ? 'text-primary fw-bold' : 'text-field-mid'}
                          >
                            {child?.name}
                          </span>
                        </ListItemButton>
                      );
                    };

                    return (
                      <>
                        {visibleChildren.map((child: any, i: number) => renderChildItem(child, i))}
                        {lockedChildren.length > 0 && (
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              minHeight: 'calc(100vh - 220px)',
                            }}
                          >
                            {lockedChildren.map((child: any, i: number) => (
                              <React.Fragment key={`locked-${i}`}>
                                {renderChildItem(child, i, { locked: true })}
                              </React.Fragment>
                            ))}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(76, 92, 107, 0.78)',
                                borderRadius: '8px',
                                m: '0 8px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 3,
                                px: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: '50%',
                                  bgcolor: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mb: 2,
                                }}
                              >
                                <LockIcon sx={{ fontSize: 28, color: '#007bff' }} />
                              </Box>
                              <Box
                                component="span"
                                sx={{
                                  fontSize: '1.15rem',
                                  fontWeight: 600,
                                  color: '#fff',
                                  textAlign: 'center',
                                  mb: 0.5,
                                }}
                              >
                                Unlock
                              </Box>
                              <Box
                                component="span"
                                sx={{
                                  fontSize: '1.1rem',
                                  fontWeight: 700,
                                  color: '#fff',
                                  textAlign: 'center',
                                  mb: 1.5,
                                }}
                              >
                                Management Features
                              </Box>
                              <Box
                                component="span"
                                sx={{
                                  fontSize: '0.875rem',
                                  color: 'rgba(255,255,255,0.95)',
                                  textAlign: 'center',
                                  mb: 2,
                                  lineHeight: 1.5,
                                  maxWidth: 260,
                                }}
                              >
                                Upgrade to Silver+ to save unlimited programs, manage your applications, requirements, and set personal reminders.
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', maxWidth: 140 }}>
                                <Box
                                  component="button"
                                  onClick={() => history.push(appRoutes.upgradeToSilverPlus.path)}
                                  sx={{
                                    py: 1,
                                    px: 2,
                                    borderRadius: '8px',
                                    border: 'none',
                                    bgcolor: '#007bff',
                                    color: '#fff',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                  }}
                                >
                                  View Plans
                                </Box>
                                <Box
                                  component="button"
                                  onClick={() => window.open('https://www.gradmap.com/pricing/', '_blank')}
                                  sx={{
                                    py: 1,
                                    px: 2,
                                    borderRadius: '8px',
                                    border: '1px solid #dee2e6',
                                    bgcolor: '#fff',
                                    color: '#333',
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                  }}
                                >
                                  Learn More
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </List>
              </Collapse>
            )}
          </div>
        );
      })}
    </List>
  );
}
